/**
 * Admin page for managing clauses within templates.
 *
 * Provides CRUD operations for clauses:
 * - Template selector showing both global and org-scoped templates
 * - Clause list with edit/delete actions grouped by section with text-h4 headers
 * - Create/edit clause via ClauseForm (shared Modal component)
 * - Delete confirmation via shared Modal component
 * - Admin role requirement (shows access denied for non-admins)
 * - "In use" badge on a row whose clause id has entries in
 *   parseClauseTags()'s referencingTagsByClauseId (org templates only —
 *   global templates have no bodyTags); the same lookup feeds
 *   referencingTags/referencedOptionIds into ClauseForm when editing a
 *   referenced clause, so the author sees the referencing context before
 *   renaming
 * - Unknown-tag context: clicking "Add Clause" beside a specific unknown
 *   tag in UnusedTagsPanel carries that tag through as `sourceTag`, shown to
 *   ClauseForm as a create-mode info banner. Cleared on modal close and on
 *   a plain "Create Clause" so the banner never goes stale.
 * - Guided rename migration: owns ClauseMigrationDialog, opened via
 *   ClauseForm's "Migrate & rename…" CTA (referencing banner or 409 block)
 *   for whichever clause is currently being edited. On a successful
 *   migration (`onMigrated`) the ClauseForm is closed immediately — it is
 *   bound to the now-dead old clause id — while the migration dialog stays
 *   open to show its own success summary until dismissed.
 * - Deep link from the Template Health hub (TemplateDetail): `?template=<id>`
 *   preselects the dropdown, `?sourceTag=<tag>` (only honoured together with
 *   a valid `template`) opens the create modal with that tag as context. Both
 *   params are stripped from the URL once evaluated (same idiom as
 *   useSowScopedCreateAction: a Pattern 0 render-time guard fires the seed
 *   exactly once per distinct template id — no `set-state-in-effect` risk —
 *   and a separate setState-free effect strips the URL, since setSearchParams
 *   is a real router-mutation side effect that belongs in an effect). An
 *   unrecognised `template` id is a silent no-op: no selection change, no
 *   modal, but the params are still stripped so the address bar doesn't keep
 *   re-offering a dead link.
 * Uses brand typography scale, .input/.label classes, and colour tokens.
 */

import { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { Info } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTemplates, useTemplateWithSchema, useClauses, useDeleteClause } from '../../hooks';
import { useOrgTemplates, useOrgTemplate } from '../../hooks/useOrgTemplates';
import { Card, Button, LoadingSpinner, Modal } from '../../components/common';
import { getClauseConflictTags } from '../../services/api';
import ClauseForm from '../../components/admin/ClauseForm';
import ClauseMigrationDialog from '../../components/admin/ClauseMigrationDialog';
import ConflictTagList from '../../components/admin/ConflictTagList';
import UnusedTagsPanel from '../../components/admin/UnusedTagsPanel';
import { parseClauseTags, findOrphanedClauseIds } from '../../utils/clauseTagParser';
import type { Clause } from '../../types';

export default function ClauseManager() {
  const { user } = useAuth();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingClause, setEditingClause] = useState<Clause | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Clause | null>(null);
  const [deleteError, setDeleteError] = useState<{
    message: string;
    tags?: string[];
  } | null>(null);
  const [isOrgTemplate, setIsOrgTemplate] = useState(false);
  const [initialClauseId, setInitialClauseId] = useState<string | undefined>();
  const [sourceTag, setSourceTag] = useState<string | undefined>();
  const [migratingClause, setMigratingClause] = useState<Clause | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: templatesData, isLoading: templatesLoading } = useTemplates();
  const { data: orgTemplatesData, isLoading: orgTemplatesLoading } = useOrgTemplates();
  const { data: clausesData, isLoading: clausesLoading } = useClauses(selectedTemplateId);
  const { data: orgTemplateDetail } = useOrgTemplate(isOrgTemplate ? selectedTemplateId : '');
  const { data: globalTemplateDetail } = useTemplateWithSchema(
    !isOrgTemplate && selectedTemplateId ? selectedTemplateId : ''
  );
  const deleteClause = useDeleteClause();

  const globalTemplates = templatesData?.items || [];
  const orgTemplates = (orgTemplatesData?.items || []).filter((t) => t.isActive);

  // Deep-link preselect from the Template Health hub — see file-top comment.
  // Matching a `template` id requires the org template list to have loaded,
  // so the signal only "arrives" once `orgTemplatesLoading` clears.
  const templateParam = searchParams.get('template');
  const sourceTagParam = searchParams.get('sourceTag');
  const deepLinkSignalKey =
    templateParam && !orgTemplatesLoading ? templateParam : null;
  const [handledDeepLink, setHandledDeepLink] = useState<string | null>(null);
  if (deepLinkSignalKey !== null && deepLinkSignalKey !== handledDeepLink) {
    setHandledDeepLink(deepLinkSignalKey);
    const matchedTemplate = orgTemplates.some((t) => t.id === deepLinkSignalKey);
    if (matchedTemplate) {
      setSelectedTemplateId(deepLinkSignalKey);
      setIsOrgTemplate(true);
      if (sourceTagParam) {
        setSourceTag(sourceTagParam);
        setIsFormOpen(true);
      }
    }
  }

  useEffect(() => {
    if (!searchParams.has('template') && !searchParams.has('sourceTag')) return;
    if (orgTemplatesLoading) return;
    const next = new URLSearchParams(searchParams);
    next.delete('template');
    next.delete('sourceTag');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, orgTemplatesLoading]);

  // Hoist optional-chain accesses to consts so the React Compiler can infer
  // deps that match the manual dep arrays exactly.
  const orgSchedules = orgTemplateDetail?.schema?.schedules;
  const globalSchedules = globalTemplateDetail?.schema?.schedules;
  const orgModules = orgTemplateDetail?.schema?.modules;
  const globalModules = globalTemplateDetail?.schema?.modules;
  const orgBodyTags = orgTemplateDetail?.bodyTags;
  const clauseItems = clausesData?.items;

  // Unified schedules from either org or global template schema
  const schedules = useMemo(() => {
    if (isOrgTemplate) {
      return orgSchedules || [];
    }
    return globalSchedules || [];
  }, [isOrgTemplate, orgSchedules, globalSchedules]);

  // Unified modules from either org or global template schema — same source
  // the wizard (ClauseConfigurator) uses, passed to ClauseForm for the
  // module-association dropdown and used here for moduleId group headings.
  const modules = useMemo(() => {
    if (isOrgTemplate) {
      return orgModules || [];
    }
    return globalModules || [];
  }, [isOrgTemplate, orgModules, globalModules]);

  const moduleNameById = useMemo(() => {
    const map: Record<string, string> = {};
    modules.forEach((m) => {
      map[m.id] = m.name;
    });
    return map;
  }, [modules]);

  // A module clause's association is orderFormId (legacy) or moduleId — the
  // two are mutually exclusive, so this key covers both for sorting/grouping.
  const associationKey = (clause: Clause): string => clause.orderFormId || clause.moduleId || '';

  // Sort clauses: contract-details first, then module clauses grouped by
  // their association (orderFormId or moduleId), then by refNum/order
  const clauses = useMemo(() => {
    const items = clauseItems || [];
    return [...items].sort((a, b) => {
      // Contract-details before module
      if (a.section !== b.section) {
        return a.section === 'contract-details' ? -1 : 1;
      }
      // Within module section, group by association
      if (a.section === 'module' && associationKey(a) !== associationKey(b)) {
        return associationKey(a).localeCompare(associationKey(b), undefined, { numeric: true });
      }
      // Within same group, sort by referenceNumber then order
      const refCompare = a.referenceNumber.localeCompare(b.referenceNumber, undefined, {
        numeric: true,
      });
      if (refCompare !== 0) return refCompare;
      return (a.order ?? 0) - (b.order ?? 0);
    });
  }, [clauseItems]);

  const { unknownTags, ambiguousTags, orphanedIds, referencingTagsByClauseId, referencedOptionIdsByClauseId } = useMemo(() => {
    if (!isOrgTemplate || !orgBodyTags) {
      return {
        unknownTags: [],
        ambiguousTags: [],
        orphanedIds: [],
        referencingTagsByClauseId: {} as Record<string, string[]>,
        referencedOptionIdsByClauseId: {} as Record<string, string[]>,
      };
    }
    const clauseRefs = clauses.map((c) => ({
      id: c.id,
      options: c.options.map((o) => ({ id: o.id })),
    }));
    const parsed = parseClauseTags(orgBodyTags, clauseRefs);
    return {
      unknownTags: parsed.unknownTags,
      ambiguousTags: parsed.ambiguousTags,
      orphanedIds: findOrphanedClauseIds(
        parsed.referencedClauseIds,
        clauses.map((c) => c.id)
      ),
      referencingTagsByClauseId: parsed.referencingTagsByClauseId,
      referencedOptionIdsByClauseId: parsed.referencedOptionIdsByClauseId,
    };
  }, [isOrgTemplate, orgBodyTags, clauses]);

  // Admin check
  if (user?.role !== 'admin') {
    return (
      <div className="space-y-6">
        <h1 className="text-h1 text-slate-900 dark:text-slate-50">Clause Manager</h1>
        <Card>
          <p className="text-red-600">Access Denied. Admin role required.</p>
        </Card>
      </div>
    );
  }

  const handleCreateClick = () => {
    setEditingClause(null);
    setInitialClauseId(undefined);
    setSourceTag(undefined);
    setIsFormOpen(true);
  };

  // Opening the create form from an unknown tag deliberately carries NO clause
  // ID: a tag like clause_a_b_c cannot be split back into one clause ID, so the
  // administrator types the intended ID against the tag shown in the panel.
  // The tag itself still rides along as sourceTag context for ClauseForm's banner.
  const handleAddClauseFromTag = (tag: string) => {
    setEditingClause(null);
    setInitialClauseId(undefined);
    setSourceTag(tag);
    setIsFormOpen(true);
  };

  const handleEditClick = (clause: Clause) => {
    setEditingClause(clause);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (clause: Clause) => {
    setDeleteError(null);
    setDeleteConfirm(clause);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm && selectedTemplateId) {
      setDeleteError(null);
      try {
        await deleteClause.mutateAsync({
          templateId: selectedTemplateId,
          clauseId: deleteConfirm.id,
        });
        setDeleteConfirm(null);
      } catch (err) {
        // A 409 from the active-template lifecycle guard keeps the dialog open
        // and names the tags that must be migrated before the clause can go.
        // getClauseConflictTags only matches ApiError (an Error), so a
        // conflict always has err.message — no separate conflict fallback arm.
        const conflictTags = getClauseConflictTags(err);
        setDeleteError({
          message: err instanceof Error ? err.message : 'Failed to delete clause.',
          ...(conflictTags ? { tags: conflictTags } : {}),
        });
      }
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingClause(null);
    setInitialClauseId(undefined);
    setSourceTag(undefined);
  };

  const handleMigrateClick = () => {
    setMigratingClause(editingClause);
  };

  // The migration succeeded — the ClauseForm underneath is bound to the now-dead
  // old clause id, so it closes immediately. The migration dialog itself stays
  // open (it shows its own success summary) until the author dismisses it.
  const handleMigrated = () => {
    handleFormClose();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-h1 text-slate-900 dark:text-slate-50">Clause Manager</h1>
        <Button onClick={handleCreateClick} disabled={!selectedTemplateId}>
          Create Clause
        </Button>
      </div>

      {/* Tag Syntax Guide */}
      <Card>
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-brand-500 shrink-0 mt-0.5" />
          <div className="text-body-sm text-slate-600 dark:text-slate-400">
            <p className="font-medium text-slate-900 dark:text-slate-100 mb-2">Template Tag Syntax</p>
            <p className="mb-2">
              Each clause option needs matching conditional tags in the Word template.
              Use the clause ID and option ID to form the tag name:
            </p>
            <div className="bg-slate-50 dark:bg-slate-800 rounded-md p-3 font-mono text-caption space-y-1">
              <p><span className="text-brand-600">{'{#clause_[clauseId]_[optionId]}'}</span> &mdash; opens a conditional block</p>
              <p><span className="text-brand-600">{'{/clause_[clauseId]_[optionId]}'}</span> &mdash; closes the block</p>
            </div>
            <p className="mt-2">
              <span className="font-medium">Example:</span> A clause with ID{' '}
              <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">liquidated-damages</code> and option{' '}
              <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">yes</code> becomes:
            </p>
            <div className="bg-slate-50 dark:bg-slate-800 rounded-md p-3 font-mono text-caption mt-1">
              <p><span className="text-brand-600">{'{#clause_liquidated-damages_yes}'}</span></p>
              <p className="text-slate-400">...clause text for this option...</p>
              <p><span className="text-brand-600">{'{/clause_liquidated-damages_yes}'}</span></p>
            </div>
            <p className="mt-2 text-slate-500">
              When an option is selected, only that option&apos;s wording remains. When the clause is
              left unanswered, every option&apos;s wording stays, so the document reads exactly like
              the original template. Wrap drafting guidance that should disappear once any answer is
              given in{' '}
              <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">
                {'{#unanswered_liquidated-damages}'}
              </code>{' '}…{' '}
              <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">
                {'{/unanswered_liquidated-damages}'}
              </code>
              .
            </p>
            <p className="mt-2 text-slate-500">
              <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">
                {'{check_[clauseId]_[optionId]}'}
              </code>{' '}
              is a plain marker (never a section like{' '}
              <code className="bg-slate-100 dark:bg-slate-700 px-1 rounded">
                {'{#check_...}'}
              </code>
              ) placed beside a real Word legacy checkbox (Developer → Legacy Tools → Check Box
              Form Field). Generation checks or unchecks that box based on the selected option.
            </p>
            <p className="mt-2 text-slate-500">
              The preview wording shown for each option is derived from the uploaded template
              document and refreshes when a new version is uploaded.
            </p>
          </div>
        </div>
      </Card>

      {/* Template Selector */}
      <Card>
        <label htmlFor="template-select" className="label mb-2">
          Template
        </label>
        <select
          id="template-select"
          value={selectedTemplateId}
          onChange={(e) => {
            const id = e.target.value;
            setSelectedTemplateId(id);
            setIsOrgTemplate(orgTemplates.some((t) => t.id === id));
          }}
          className="input"
          disabled={templatesLoading && orgTemplatesLoading}
        >
          <option value="">Select a template...</option>
          {globalTemplates.length > 0 && (
            <optgroup label="Global Templates">
              {globalTemplates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </optgroup>
          )}
          {orgTemplates.length > 0 && (
            <optgroup label="Organisation Templates">
              {orgTemplates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      </Card>

      {/* Unused Tags Panel (org templates only) */}
      {isOrgTemplate && selectedTemplateId && !clausesLoading && orgTemplateDetail?.bodyTags && (
        <UnusedTagsPanel
          unknownTags={unknownTags}
          ambiguousTags={ambiguousTags}
          orphanedClauseIds={orphanedIds}
          onAddClause={handleAddClauseFromTag}
        />
      )}

      {/* Loading State */}
      {clausesLoading && selectedTemplateId && (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      )}

      {/* Empty State */}
      {!clausesLoading && selectedTemplateId && clauses.length === 0 && (
        <Card>
          <p className="text-slate-500 dark:text-slate-400 text-center py-4">No clauses found for this template.</p>
        </Card>
      )}

      {/* Clause List */}
      {!clausesLoading && clauses.length > 0 && (
        <div className="space-y-4">
          {clauses.map((clause, index) => {
            // Determine if we need a section header before this clause
            const prevClause = index > 0 ? clauses[index - 1] : null;
            const groupKey = clause.section === 'module' ? `module-${associationKey(clause)}` : 'contract-details';
            const prevGroupKey = prevClause
              ? prevClause.section === 'module' ? `module-${associationKey(prevClause)}` : 'contract-details'
              : null;
            const showHeader = groupKey !== prevGroupKey;

            const sectionLabel = clause.section === 'contract-details'
              ? 'Contract Details'
              : clause.orderFormId
                ? `Module Order Form ${clause.orderFormId.toUpperCase()}`
                : `Module: ${moduleNameById[clause.moduleId || ''] ?? clause.moduleId}`;

            return (
              <div key={clause.id}>
                {showHeader && (
                  <h2 className={`text-h4 text-slate-800 dark:text-slate-200 border-b border-slate-200 dark:border-slate-700 pb-2 ${index > 0 ? 'mt-6' : ''}`}>
                    {sectionLabel}
                  </h2>
                )}
                <Card className={showHeader ? 'mt-3' : ''}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-h3 text-slate-900 dark:text-slate-100 flex items-center gap-2">
                        {clause.name}
                        {(referencingTagsByClauseId[clause.id]?.length ?? 0) > 0 && (
                          <span className="badge-draft">In use</span>
                        )}
                      </h3>
                      <div className="mt-1 text-body-sm text-slate-500 dark:text-slate-400 space-x-4">
                        <span className="capitalize">{clause.section}</span>
                        <span>Ref: {clause.referenceNumber}</span>
                        {clause.orderFormId && <span>Order Form: {clause.orderFormId}</span>}
                        {clause.moduleId && (
                          <span>Module: {moduleNameById[clause.moduleId] ?? clause.moduleId}</span>
                        )}
                      </div>
                      {clause.instructionalText && (
                        <p className="mt-2 text-body-sm text-slate-600 dark:text-slate-400">{clause.instructionalText}</p>
                      )}
                      <div className="mt-2 text-body-sm text-slate-500 dark:text-slate-400">
                        {clause.options.length} option{clause.options.length !== 1 ? 's' : ''}
                        {clause.isRequired && (
                          <span className="ml-2 text-red-500 dark:text-red-400 font-medium">Required</span>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <Button variant="secondary" size="sm" onClick={() => handleEditClick(clause)}>
                        Edit
                      </Button>
                      <Button variant="danger" size="sm" onClick={() => handleDeleteClick(clause)}>
                        Delete
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Form Modal */}
      {isFormOpen && selectedTemplateId && (
        <ClauseForm
          templateId={selectedTemplateId}
          clause={editingClause}
          onClose={handleFormClose}
          initialId={initialClauseId}
          schedules={schedules}
          modules={modules}
          referencingTags={editingClause ? referencingTagsByClauseId[editingClause.id] : undefined}
          referencedOptionIds={editingClause ? referencedOptionIdsByClauseId[editingClause.id] : undefined}
          sourceTag={sourceTag}
          onMigrate={handleMigrateClick}
        />
      )}

      {/* Guided rename migration dialog */}
      {migratingClause && selectedTemplateId && (
        <ClauseMigrationDialog
          templateId={selectedTemplateId}
          clause={migratingClause}
          referencingTags={referencingTagsByClauseId[migratingClause.id]}
          referencedOptionIds={referencedOptionIdsByClauseId[migratingClause.id]}
          onClose={() => setMigratingClause(null)}
          onMigrated={handleMigrated}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => {
          setDeleteConfirm(null);
          setDeleteError(null);
        }}
        title="Delete Clause"
      >
        <p className="text-slate-600 dark:text-slate-400 mb-6">
          Are you sure you want to delete &ldquo;{deleteConfirm?.name}&rdquo;? This action cannot be undone.
        </p>
        {deleteError && (
          <div
            role="alert"
            className="mb-6 p-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-body-sm text-red-600 dark:text-red-400"
          >
            <p>{deleteError.message}</p>
            {deleteError.tags && <ConflictTagList tags={deleteError.tags} />}
          </div>
        )}
        <div className="flex justify-end space-x-3">
          <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} loading={deleteClause.isPending}>
            Confirm
          </Button>
        </div>
      </Modal>
    </div>
  );
}
